import { call, put, takeEvery, takeLatest } from "redux-saga/effects";

// Login Redux States
import { LoginTypes } from "./actionTypes";
import { apiError, loginSuccess, logoutUserSuccess } from "./actions";

//Include Both Helper File with needed methods
import { getFirebaseBackend } from "../../../helpers/firebase_helper";
import {
  postFakeLogin,
  postJwtLogin,
  postSocialLogin,
} from "../../../helpers/fakebackend_helper";
import { loginWithWordPress, storePendingEmailOtp } from "../../../helpers/wordpress_auth";
import { storePendingTotp } from "../../../helpers/wordpress_totp";
import { clearSessionActivity } from "../../../helpers/session_activity";
import { clearAuthUser, setAuthUserString } from "../../../helpers/auth_storage";
import { getRememberMePreference } from "../../../helpers/remember_me";

const fireBaseBackend = getFirebaseBackend();

/** React Router v6 passes navigate(); older code passed history.push(). */
const goTo = (history: any, path: string) => {
  if (typeof history === "function") {
    history(path);
    return;
  }
  if (history && typeof history.push === "function") {
    history.push(path);
  }
};

function* loginUser({ payload: { user, history } }: any): Generator<any, void, any> {
  try {
    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const response: Promise<any> = yield call(
        fireBaseBackend.loginUser,
        user.email,
        user.password
      );
      yield put(loginSuccess(response));
    } else if (process.env.REACT_APP_DEFAULTAUTH === "jwt") {
      const response: Promise<any> = yield call(postJwtLogin, {
        email: user.email,
        password: user.password,
      });
      setAuthUserString(JSON.stringify(response), getRememberMePreference());
      yield put(loginSuccess(response));
    } else if (process.env.REACT_APP_DEFAULTAUTH === "wordpress") {
      // WordPress GraphQL authentication (email OTP when enabled on WP)
      const result: any = yield call(
        loginWithWordPress,
        user.email,
        user.password
      );

      if (result?.status === "otp_required") {
        storePendingEmailOtp({
          challengeToken: result.challengeToken,
          emailHint: result.emailHint,
          username: result.username,
          password: result.password,
        });
        // Clear login loading state (LOGIN_SUCCESS only toggles loading).
        yield put(loginSuccess({ pendingEmailOtp: true }));
        goTo(history, "/email-otp");
        return;
      }

      if (result?.status === "totp_required") {
        storePendingTotp({
          challengeToken: result.challengeToken,
          mode: "verify",
          username: result.username,
          password: result.password,
        });
        yield put(loginSuccess({ pendingTotp: true }));
        goTo(history, "/totp-verify");
        return;
      }

      if (result?.status === "totp_setup_required") {
        storePendingTotp({
          challengeToken: result.challengeToken,
          mode: "setup",
          username: result.username,
          password: result.password,
        });
        yield put(loginSuccess({ pendingTotpSetup: true }));
        goTo(history, "/totp-setup");
        return;
      }

      if (result?.status !== "authenticated" || !result?.authData) {
        yield put(apiError("Invalid username or password."));
        return;
      }

      yield put(loginSuccess(result.authData));
      goTo(history, "/dashboard");
      return;
    } else if (process.env.REACT_APP_DEFAULTAUTH === "fake") {
      const response: Promise<any> = yield call(postFakeLogin, {
        email: user.email,
        password: user.password,
      });
      setAuthUserString(JSON.stringify(response), getRememberMePreference());
      yield put(loginSuccess(response));
    }
    goTo(history, "/dashboard");
  } catch (error: any) {
    const raw =
      error instanceof Error
        ? error.message
        : typeof error === "string"
        ? error
        : error?.message || String(error || "Login failed");
    const lower = String(raw).toLowerCase();
    const errorMessage =
      lower.includes("invalid") ||
      lower.includes("password") ||
      lower.includes("username") ||
      lower.includes("credentials")
        ? "Invalid username or password."
        : raw;
    yield put(apiError(errorMessage));
  }
}

function* logoutUser({ payload: { history } }: any) {
  try {
    clearAuthUser();
    clearSessionActivity();

    // Clear stored credentials for re-login
    try {
      const { clearStoredCredentials } = require("../../../helpers/auth_relogin");
      clearStoredCredentials();
    } catch (error) {
      // Silently fail if helper not available
    }

    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const response: Promise<any> = yield call(fireBaseBackend.logout);
      yield put(logoutUserSuccess(response));
    }
    goTo(history, "/login");
  } catch (error: any) {
    // Ensure error is always a string
    const errorMessage = error instanceof Error 
      ? error.message 
      : typeof error === 'string' 
      ? error 
      : error?.message || String(error || 'Logout failed');
    yield put(apiError(errorMessage));
  }
}

function* socialLogin({ payload: { type, history } }: any): Generator<any> {
  try {
    let response;
    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const fireBaseBackend = getFirebaseBackend();
     const response = yield call(fireBaseBackend.socialLoginUser, type);
      if (response) {
        goTo(history, "/dashboard");
      } else {
        goTo(history, "/login");
      }
      setAuthUserString(JSON.stringify(response), getRememberMePreference());
      yield put(loginSuccess(response));
    }
    if (response) {
      goTo(history, "/dashboard");
    }
  } catch (error: any) {
    // Ensure error is always a string
    const errorMessage = error instanceof Error 
      ? error.message 
      : typeof error === 'string' 
      ? error 
      : error?.message || String(error || 'Social login failed');
    yield put(apiError(errorMessage));
  }
}


function* authSaga() {
  // takeLatest prevents double-submit from sending two different OTP emails.
  yield takeLatest(LoginTypes.LOGIN_USER, loginUser);
  yield takeLatest(LoginTypes.SOCIAL_LOGIN, socialLogin);
  yield takeEvery(LoginTypes.LOGOUT_USER, logoutUser);
}

export default authSaga;
